import React, { Children, PureComponent } from 'react';
import PropTypes from '@catalytic/pushbot-ui/utils/PropTypes';
import Dimensions from 'react-dimensions';
import classNames from 'classnames';
import { createStyleSheet } from 'jss-theme-reactor';
import Helmet from 'react-helmet';
import { compose } from 'recompose';

import AppBar from '@catalytic/pushbot-ui/AppBar';
import AppDrawer from '@catalytic/pushbot-ui/AppDrawer';
import Dialog from '@catalytic/pushbot-ui/Dialog';
import Form from '@catalytic/pushbot-ui/Form';
import IconButton from '@catalytic/pushbot-ui/IconButton';
import Input from '@catalytic/pushbot-ui/Input';
import Layout from '@catalytic/pushbot-ui/Layout';
import Back from '@catalytic/pushbot-ui/SvgIcon/Back';
import Logo from '@catalytic/pushbot-ui/SvgIcon/Catalytic/Logo';
import Menu from '@catalytic/pushbot-ui/SvgIcon/Menu';
import SearchIcon from '@catalytic/pushbot-ui/SvgIcon/Search';
import Toolbar from '@catalytic/pushbot-ui/Toolbar';

import ListContent from './ListContent';
import { SnackbarContainer } from '../Snackbar';
import ModalSheet from '../ModalSheet/ModalSheet';
import ModalSheetClose from '../ModalSheet/ModalSheetClose';
import ModalSheetContent from '../ModalSheet/ModalSheetContent';
import ModalSheetHeader from '../ModalSheet/ModalSheetHeader';
import ModalSheetHeaderTitle from '../ModalSheet/ModalSheetHeaderTitle';
import withEmbeddedMode from '../util/withEmbeddedMode';
import env from '../env';

export const styleSheet = createStyleSheet('AppStructure', theme => ({
  '@global': theme.mixins.global(),
  root: {
    display: 'flex',
    alignItems: 'stretch',
    height: '100%',
    width: '100%',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.leavingScreen,
      easing: theme.transitions.easing.sharp
    }),
    ...theme.typography.body1
  },
  appBar: {
    display: 'flex',
    transition: theme.transitions.create(['width', 'background-color', 'color'])
  },
  buttonBox: {
    display: 'flex'
  },
  content: {
    paddingTop: '0',
    paddingBottom: theme.typography.rhythmByUnit(7),
    flexGrow: 1,
    flex: '1 1 100%',
    maxWidth: '100%',
    margin: '0 auto',
    overflowY: 'auto',
    '-webkitOverflowScrolling': 'touch'
  },
  contentFullWidth: {
    paddingBottom: 0
  },
  contentDetail: {
    paddingTop: 0,
    paddingBottom: 0
  },
  drawer: {
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.leavingScreen,
      easing: theme.transitions.easing.sharp
    })
  },
  drawerOpen: {
    position: 'fixed',
    height: '100vh',
    opacity: 1,
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.enteringScreen,
      easing: theme.transitions.easing.easeOut
    })
  },
  detailAppBar: {
    display: 'none'
  },
  gutters: theme.mixins.gutters(4),
  guttersRoot: {},
  layoutDetail: {
    marginTop: 0
  },
  open: {
    transform: `translate3d(${theme.variables.drawerWidth}px, 0, 0)`,
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.enteringScreen,
      easing: theme.transitions.easing.easeOut
    })
  },
  paper: {
    background: theme.palette.background.paper
  },
  searchForm: {
    flex: 1,
    opacity: 0,
    visibility: 'hidden',
    transition: theme.transitions.create('opacity')
  },
  searchFormFocus: {
    opacity: 1,
    visibility: 'visible'
  },
  searchInput: {
    backgroundColor: theme.palette.background.searchInput,
    color: theme.palette.getContrastText(theme.palette.background.searchInput),
    borderRadius: theme.variables.inputBorderRadius,
    flex: 1,
    width: '100%',
    height: theme.variables.searchInputHeight,
    padding: `0 ${theme.typography.rhythmByUnit(1)}`,
    boxSizing: 'border-box',
    '&:hover': {
      backgroundColor: theme.palette.background.searchInput
    },
    '& input': {
      color: theme.palette.getContrastText(
        theme.palette.background.searchInput
      ),
      ...theme.mixins.placeholder({
        color: theme.palette.grey[200]
      })
    }
  },
  searchInputFocus: {
    '&, &:hover': {
      backgroundColor: theme.palette.background.searchInput
    },
    '& input': {
      color: theme.palette.getContrastText(
        theme.palette.background.searchInput
      ),
      ...theme.mixins.placeholder({
        color: theme.palette.grey[200]
      })
    }
  },
  searchInputElement: {
    height: theme.variables.searchInputHeight
  },
  toolbar: {
    justifyContent: 'space-between'
  },
  userAvatar: {
    fontSize: theme.variables.searchInputHeight
  },
  userAvatarHiddenOnMobile: {},
  [theme.breakpoints.up('xs')]: {
    searchInput: {
      backgroundImage: SearchIcon.toURL({
        fill: theme.palette.getContrastText(
          theme.palette.background.searchInput
        ),
        height: '1em',
        width: '1em'
      }),
      backgroundRepeat: 'no-repeat',
      backgroundPositionX: '1em',
      backgroundPositionY: '50%',
      backgroundSize: '1em',
      paddingLeft: theme.typography.rhythmByUnit(4)
    },
    searchInputFocus: {
      backgroundImage: SearchIcon.toURL({
        fill: theme.palette.getContrastText(
          theme.palette.background.searchInput
        ),
        height: '1em',
        width: '1em',
        opacity: 0.6
      }),
      backgroundColor: theme.palette.background.searchInput
    }
  },
  [theme.breakpoints.up('sm')]: {
    content: {
      paddingBottom: theme.variables.contentPadding
    },
    contentFullWidth: {
      paddingBottom: 0
    },
    detailAppBar: {
      display: 'flex'
    },
    guttersRoot: theme.mixins.gutters(1),
    toolbar: {
      ...theme.mixins.gutters(3),
      paddingLeft: theme.typography.rhythmByUnit(1.5)
    }
  },
  [theme.breakpoints.down('sm')]: {
    content: {
      overflowX: 'hidden'
    },
    userAvatarHiddenOnMobile: theme.mixins.hidden(),
    toolbar: {
      ...theme.mixins.gutters(1),
      paddingLeft: theme.typography.rhythmByUnit(0.5)
    }
  },
  [theme.breakpoints.up('md')]: {
    buttonBoxMargin: {
      marginLeft: 'auto'
    },
    drawerIcon: {
      opacity: 1,
      visibility: 'visible',
      transition: theme.transitions.create('opacity')
    },
    drawerIconHidden: {
      opacity: 0,
      visibility: 'hidden'
    },
    guttersRoot: theme.mixins.gutters(2),
    layoutMaxWidth: {
      minWidth: 0,
      maxWidth: theme.breakpoints.md,
      width: '100%'
    },
    open: {
      transform: 'translate3d(0, 0, 0)',
      transition: 'none'
    },
    searchForm: {
      flex: '0 1 auto',
      marginLeft: 'auto',
      visibility: 'visible',
      opacity: 1
    },
    searchInput: {
      marginRight: theme.typography.rhythmByUnit(1)
    },
    searchInputFocus: {
      color: theme.palette.getContrastText(theme.palette.background.searchInput)
    }
  },
  [theme.breakpoints.up('xl')]: {
    open: {
      transform: `translate3d(${theme.variables.drawerWidthLarge}px, 0, 0)`
    }
  }
}));

export class AppStructure extends PureComponent {
  static displayName = 'AppStructure';

  static propTypes = {
    /**
     * If true the app bar is displayed
     */
    appBar: PropTypes.bool,
    /**
     * Component to be used as the appBar
     */
    appBarComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    /**
     * Component to be used for the notifications component
     */
    appBarNotifications: PropTypes.node,
    /**
     * Component to be used for the secondary component
     */
    appBarSecondary: PropTypes.node,
    /**
     * Component to be used for the user avatar component
     */
    appBarUserAvatar: PropTypes.node,
    /**
     * If true the App Drawer is displayed
     */
    appDrawer: PropTypes.bool,
    /**
     * Can be any node or number of nodes.
     */
    appDrawerChildren: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.arrayOf(PropTypes.node)
    ]),
    /**
     * Icon for App Drawer logo area
     */
    appDrawerIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    /**
     * Name of application
     */
    appName: PropTypes.string,
    /**
     *  function invoked when parent page title is clicked
     */
    breadcrumbOnClick: PropTypes.func,
    /**
     *  Title of parent page for Breadcrumb component
     */
    breadcrumbParent: PropTypes.string,
    /**
     * Can be any node or number of nodes.
     */
    children: PropTypes.node,
    /**
     * Class name passed app
     */
    className: PropTypes.string,
    /**
     * Width of the browser window passed in by react-dimensions
     */
    containerWidth: PropTypes.number,
    /**
     * Height of the browser window passed in by react-dimensions
     */
    containerHeight: PropTypes.number,
    /**
     * Class name passed app content container
     */
    contentClassName: PropTypes.string,
    /**
     * Display title and content in DetailContent
     */
    detail: PropTypes.bool,
    /**
     * Actions to display in top right of DetailContent header
     */
    detailHeaderActions: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.arrayOf(PropTypes.node)
    ]),
    /**
     * Display DetailContent header with minimized top and bottom gutters
     */
    detailHeaderDense: PropTypes.bool,
    /**
     * Display divider underneath DetailContent header
     */
    detailHeaderDivider: PropTypes.bool,
    /**
     * Display drawer icon in DetailContent header
     */
    detailHeaderDrawer: PropTypes.bool,
    /**
     * Truncate text in DetailContent header
     */
    detailHeaderTruncate: PropTypes.bool,
    /**
     * Hide text in DetailContent header
     */
    detailHidden: PropTypes.bool,
    /**
     * Icon/avatar to display in DetailContent header
     */
    detailIcon: PropTypes.node,
    /**
     * Label to display next to icon in DetailContent header
     */
    detailIconLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    /**
     * If true, DetailContent body will display with no gutters so list can bleed to edges
     */
    detailList: PropTypes.bool,
    /**
     *  DetailContent header primary detail text/node
     */
    detailPrimary: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    /**
     *  DetailContent header secondary detail text/node
     */
    detailSecondary: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    /**
     *  DetailContent header tertiary detail text/node
     */
    detailTertiary: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    /**
     * Thumbnail url for logo to display in DetailContent header
     */
    detailThumbnail: PropTypes.string,
    /**
     * Nodes to be placed in the dialog.
     */
    dialog: PropTypes.node,
    /**
     * If `true`, the dialog title will use the theme's accent color.
     */
    dialogAccent: PropTypes.bool,
    /**
     * The CSS class name of the dialog element.
     */
    dialogClassName: PropTypes.string,
    /**
     * Component to be used as the dialog node
     */
    dialogComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    /**
     * Nodes to be placed in the dialog confirmation action container.
     */
    dialogConfirmationActions: Form.propTypes.confirmationActions,
    /**
     * Component to be used as the dialog confirmation action node
     */
    dialogConfirmationActionsComponent:
      Form.propTypes.confirmationActionsComponent,
    /**
     * The CSS class name of the dialog confirmation action element.
     */
    dialogConfirmationActionsClassName: PropTypes.string,
    /**
     * If `true`, the dialog title will use the theme's error color.
     */
    dialogError: PropTypes.bool,
    /**
     * If `true`, The dialog will be full-screen.
     */
    dialogFullscreen: PropTypes.bool,
    /**
     * If `true`, the dialog title will use the theme's primary color.
     */
    dialogPrimary: PropTypes.bool,
    /**
     * If `true`, the dialog title will use the theme's secondary color.
     */
    dialogSecondary: PropTypes.bool,
    /**
     * Node to be placed in the title container.
     */
    dialogTitle: PropTypes.node,
    /**
     * Component to be used as the title node
     */
    dialogTitleComponent: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.func
    ]),
    /**
     * The CSS class name of the title element.
     */
    dialogTitleClassName: PropTypes.string,
    /**
     * Display text used in content body
     */
    display: PropTypes.string,
    /**
     * If `true`, then drawer is displayed
     */
    expandDrawer: PropTypes.bool,
    /**
     * If `true`, then search input is displayed
     */
    expandSearch: PropTypes.bool,
    /**
     * If `true`, then gutters will persist on all screen sizes
     */
    gutters: PropTypes.bool,
    /**
     * Headline text used in content body
     */
    headline: PropTypes.string,
    /**
     * Href for when pushbot logo area is clicked on medium+
     */
    homeLink: PropTypes.string,
    /**
     * Callback fired when the dialog requests to be closed.
     */
    onDialogRequestClose: PropTypes.func,
    /**
     * Callback fired when dialog is submitted
     */
    onDialogSubmit: PropTypes.func,
    /**
     * Called when App Bar Search has changed
     */
    onSearchChange: PropTypes.func,
    /**
     * Called when App Bar Search has expansion has changed
     */
    onSearchExpand: PropTypes.func,
    /**
     * Called after App Bar Search is displayed
     */
    onSearchShow: PropTypes.func,
    /**
     * Called when App Bar Search is reset
     */
    onSearchReset: PropTypes.func,
    /**
     * Called when App Bar Search is submitted
     */
    onSearchSubmit: PropTypes.func,
    /**
     * Background will use paper color
     */
    paper: PropTypes.bool,
    /**
     * A prefix used for identifying elements inside the AppStructure
     */
    prefix: PropTypes.string,
    /**
     * Search value to be displayed in the App Bar expanded search
     */
    search: PropTypes.string,
    /**
     * If `true`, then search back button is displayed in the App Bar even when search length is 0
     */
    searchBack: PropTypes.bool,
    /**
     * If `true`, then search button is displayed in the App Bar
     */
    searchButton: PropTypes.bool,
    /**
     * Search placeholder value to be displayed in the App Bar expanded search
     */
    searchPlaceholder: PropTypes.string,
    /**
     * Secondary title to be displayed in the DetailContent header
     */
    secondaryTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    /**
     * A boolean, indicating whether content should be centered at a certain width
     * with additional left/right whitespace padding.
     */
    useCenteredContentColumn: PropTypes.bool
  };

  static contextTypes = {
    styleManager: PropTypes.styleManager.isRequired
  };

  static childContextTypes = {
    appStructure: PropTypes.object.isRequired
  };

  static defaultProps = {
    appBar: true,
    appBarComponent: AppBar,
    appDrawer: false,
    appDrawerIcon: Logo,
    dialogComponent: Dialog,
    expandDrawer: false,
    expandSearch: false,
    onSearchChange: () => {},
    onSearchExpand: () => {},
    onSearchShow: () => {},
    onSearchReset: () => {},
    onSearchSubmit: () => {},
    paper: true,
    searchBack: false,
    searchButton: false,
    searchPlaceholder: 'Search',
    useCenteredContentColumn: true
  };

  state = {};

  componentWillMount() {
    const { appStructure } = this.createDefaultContext(this.props);
    this.appStructure = appStructure;
  }

  createDefaultContext() {
    return {
      appStructure: {
        contentRef: () => this.content
      }
    };
  }

  getChildContext() {
    const { appStructure } = this;
    return { appStructure };
  }

  focusSearchInput = breakpoint => {
    if (!this.searchInput || breakpoint) return;
    const { focus = () => {} } = this.searchInput;
    focus();
  };

  handleDrawer = (e, breakpoint) => {
    const { appDrawer, expandSearch } = {
      ...this.props,
      ...this.state
    };

    switch (true) {
      case (!appDrawer || breakpoint) && !expandSearch:
        return this.handleDrawerClose();
      case !expandSearch:
        return this.handleDrawerToggle(e);
      default:
        return this.handleSearchReset(e);
    }
  };

  handleDrawerClose = () => {
    this.expandDrawer(false);
  };

  handleDrawerToggle = () => {
    const { expandDrawer } = { ...this.props, ...this.state };
    this.expandDrawer(!expandDrawer);
  };

  expandDrawer(expandDrawer) {
    this.setState({ expandDrawer });
  }

  handleSearchChange = e => {
    const { onSearchChange } = this.props;
    return onSearchChange(e);
  };

  handleSearchReset = e => {
    const { onSearchReset } = this.props;
    e.preventDefault();
    return onSearchReset(e);
  };

  handleSearchSubmit = e => {
    const { onSearchSubmit } = this.props;
    e.preventDefault();
    return onSearchSubmit(e);
  };

  handleSearchBlur = () => {
    this.searchFocus(false);
  };

  handleSearchFocus = () => {
    this.searchFocus(true);
  };

  searchFocus(searchFocus) {
    this.setState({ searchFocus });
  }

  handleSearchExpand = e => {
    const { onSearchExpand, expandSearch } = this.props;
    onSearchExpand(e, expandSearch);
  };

  handleSearchShow = () => {
    const { onSearchShow } = this.props;
    return onSearchShow();
  };

  render() {
    const {
      appBar,
      appBarComponent: AppBar,
      appBarNotifications,
      appBarSecondary,
      appBarUserAvatar,
      appDrawer,
      appDrawerChildren,
      appDrawerIcon,
      appName,
      breadcrumbOnClick,
      breadcrumbParent,
      children,
      className: classNameProp,
      containerWidth,
      contentClassName: contentClassNameProp,
      detail,
      detailHeaderActions,
      detailHeaderDense,
      detailHeaderDivider,
      detailHeaderDrawer,
      detailHeaderTruncate,
      detailHidden,
      detailIcon,
      detailIconLabel,
      detailList,
      detailPrimary,
      detailSecondary,
      detailTertiary,
      detailThumbnail,
      dialog,
      dialogAccent,
      dialogClassName,
      dialogComponent: Dialog,
      dialogConfirmationActions,
      dialogConfirmationActionsClassName,
      dialogConfirmationActionsComponent,
      dialogError,
      dialogFullscreen,
      dialogPrimary,
      dialogSecondary,
      dialogTitle,
      dialogTitleClassName,
      dialogTitleComponent,
      display,
      embeddedMode,
      expandDrawer,
      expandSearch,
      gutters,
      headline,
      homeLink,
      layoutProps,
      onDialogRequestClose,
      onDialogSubmit,
      paper,
      prefix,
      progress,
      search,
      searchBack,
      searchButton,
      searchFocus,
      searchPlaceholder,
      secondaryTitle,
      title,
      useCenteredContentColumn
    } = { ...this.props, ...this.state };

    const dialogProps = {
      accent: dialogAccent,
      children: dialog,
      className: dialogClassName,
      confirmationActions: dialogConfirmationActions,
      confirmationActionsComponent: dialogConfirmationActionsComponent,
      confirmationActionsClassName: dialogConfirmationActionsClassName,
      error: dialogError,
      fullScreen: dialogFullscreen,
      primary: dialogPrimary,
      secondary: dialogSecondary,
      title: dialogTitle,
      titleComponent: dialogTitleComponent,
      titleClassName: dialogTitleClassName,
      onRequestClose: onDialogRequestClose,
      onSubmit: onDialogSubmit,
      open: !!dialog
    };

    const detailCardProps = {
      display,
      headline,
      breadcrumbOnClick,
      breadcrumbParent,
      dense: detailHeaderDense,
      detailHidden,
      detailPrimary,
      detailSecondary,
      detailTertiary,
      divider: detailHeaderDivider,
      drawer: detailHeaderDrawer,
      handleDrawer: this.handleDrawer,
      headerActions: detailHeaderActions,
      icon: detailIcon,
      iconLabel: detailIconLabel,
      list: detailList,
      thumbnail: detailThumbnail,
      progress,
      secondaryTitle,
      title,
      truncate: detailHeaderTruncate
    };

    const smallBreak =
      containerWidth >= this.context.styleManager.theme.breakpoints.sm;
    const mediumBreak =
      containerWidth >= this.context.styleManager.theme.breakpoints.md;
    const AppDrawerIcon =
      !mediumBreak && !expandSearch && appDrawer ? Menu : Back;
    const classes = this.context.styleManager.render(styleSheet);
    const appBarClassName = classNames(classes.appBar, {
      [classes.detailAppBar]: detail
    });
    const className = classNames(
      classes.root,
      {
        [classes.paper]: paper,
        [classes.open]: expandDrawer
      },
      classNameProp
    );
    const contentClassName = classNames(
      classes.content,
      {
        [classes.contentDetail]: detail,
        [classes.contentFullWidth]: !useCenteredContentColumn
      },
      contentClassNameProp
    );
    const drawerClassName = classNames(classes.drawer, {
      [classes.drawerOpen]: expandDrawer
    });
    const drawerIconClassName = classNames(classes.drawerIcon, {
      [classes.drawerIconHidden]:
        mediumBreak && !searchBack && !(search ? search.length : 0)
    });
    const guttersClassName = classNames(classes.guttersRoot, {
      [classes.gutters]: gutters
    });
    const layoutClassName = classNames({
      [classes.layoutDetail]: detail,
      [classes.layoutMaxWidth]: useCenteredContentColumn && mediumBreak
    });
    const searchFormClassName = classNames(classes.searchForm, {
      [classes.searchFormFocus]: expandSearch
    });
    const searchInputClassName = classNames(classes.searchInput, {
      [classes.searchInputFocus]: searchFocus
    });
    const buttonBoxClassName = classNames(classes.buttonBox, {
      [classes.buttonBoxMargin]: !expandSearch && (mediumBreak && !searchButton)
    });

    const combinedPrefix = prefix
      ? `ui: http://ui.pushbot.io ${prefix}`
      : 'ui: http://ui.pushbot.io';

    const content = (
      <Layout container gutter={0} justify="center">
        <Layout item className={layoutClassName} xs={12} {...layoutProps}>
          {detail ? (
            Children.map(children, child =>
              React.cloneElement(child, detailCardProps)
            )
          ) : (
            <ListContent
              {...{ display, headline: embeddedMode ? null : headline }}
              className={guttersClassName}
            >
              {children}
            </ListContent>
          )}
        </Layout>
      </Layout>
    );
    return (
      <div className={className} prefix={combinedPrefix}>
        <Helmet
          titleTemplate={`${appName} | %s`}
          defaultTitle={appName}
          title={title}
        />
        {appDrawer && (
          <AppDrawer
            className={drawerClassName}
            homeLink={homeLink}
            icon={appDrawerIcon}
            onRequestClose={this.handleDrawerClose}
            open={mediumBreak ? false : expandDrawer}
            permanent={mediumBreak}
          >
            {appDrawerChildren}
          </AppDrawer>
        )}
        <div
          className={contentClassName}
          ref={c => {
            this.content = c;
          }}
        >
          {appBar && (
            <AppBar className={appBarClassName}>
              <Toolbar className={classes.toolbar}>
                {((appBar && (!mediumBreak || searchButton)) ||
                  mediumBreak) && (
                  <IconButton
                    className={drawerIconClassName}
                    onClick={e => this.handleDrawer(e, mediumBreak)}
                  >
                    <AppDrawerIcon />
                  </IconButton>
                )}
                {(expandSearch || (searchButton && mediumBreak)) && (
                  <Form
                    className={searchFormClassName}
                    onSubmit={this.handleSearchSubmit}
                  >
                    <Input
                      id="search"
                      ref={c => {
                        this.searchInput = c;
                        this.handleSearchShow(mediumBreak);
                      }}
                      className={searchInputClassName}
                      placeholder={searchPlaceholder}
                      onBlur={this.handleSearchBlur}
                      onChange={this.handleSearchChange}
                      onFocus={this.handleSearchFocus}
                      inputClassName={classes.searchInputElement}
                      fullWidth
                      value={search}
                      underline={false}
                    />
                  </Form>
                )}
                {appBar && (
                  <div className={buttonBoxClassName}>
                    {!mediumBreak &&
                      !expandSearch &&
                      searchButton && (
                        <IconButton
                          className={classes.searchButton}
                          onClick={this.handleSearchExpand}
                        >
                          <SearchIcon />
                        </IconButton>
                      )}
                    {(smallBreak || !expandSearch) && appBarNotifications}
                    {(smallBreak || !expandSearch) && appBarSecondary}
                    {(smallBreak || !expandSearch) &&
                      appBarUserAvatar &&
                      React.cloneElement(appBarUserAvatar, {
                        className: classNames(
                          classes.userAvatar,
                          classes.userAvatarHiddenOnMobile,
                          appBarUserAvatar.props.className
                        )
                      })}
                  </div>
                )}
              </Toolbar>
            </AppBar>
          )}
          {!embeddedMode ? (
            content
          ) : (
            <ModalSheet>
              <ModalSheetHeader>
                <ModalSheetClose to={env.PUSHBOT_EMBEDDED_REFERRER} />
                <ModalSheetHeaderTitle />
              </ModalSheetHeader>
              <ModalSheetContent>{content}</ModalSheetContent>
            </ModalSheet>
          )}
        </div>
        <Dialog {...dialogProps} />
        <SnackbarContainer />
      </div>
    );
  }
}

export default compose(
  withEmbeddedMode(props => {
    const {
      appBar,
      appDrawer,
      detailHeaderDrawer,
      location,
      ...others
    } = props;

    return {
      appBar: false,
      appDrawer: false,
      detailHeaderDrawer: false,
      embeddedMode: true,
      ...others
    };
  }),
  Dimensions({
    containerStyle: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0
    },
    elementResize: true
  })
)(AppStructure);



// WEBPACK FOOTER //
// ./js/AppStructure/AppStructure.js